import React, { FC } from 'react'
import CardMedia from '@mui/material/CardMedia'
import SVGImage from './SVG'

interface Breakpoints {
  xs?: string
  sm?: string
  md?: string
  lg?: string
  xl?: string
}

type HeightType = string | Breakpoints
type WidthType = string | Breakpoints
type DisplayType = string | Breakpoints
type MarginType = string | Breakpoints

interface DefaultImageSize {
  height?: string
  width?: string
}

interface ImageProps {
  /**
   * A propriedade **`imageSrc`** define a imagem que deve ser apresentada.
   *
   * **Valor inicial**: `null`
   */
  imageSrc?: string
  /**
   * A propriedade CSS **`height`** especifica a altura de um elemento. Por padrão, a propriedade define a altura da área de conteúdo. No entanto.
   *
   * **Sintaxe**: `auto | <length> | <percentage> | min-content | max-content | fit-content | fit-content(<length-percentage>) ou de forma responsiva xs | sm | md | lg | xl`
   *
   * **Valor inicial**: `auto`
   */
  height?: HeightType
  /**
   * A propriedade CSS **`width`** define a largura de um elemento. Por padrão, ele define a largura da área de conteúdo.
   *
   * **Sintaxe**: `auto | <length> | <percentage> | min-content | max-content | fit-content | fit-content(<length-percentage>) ou de forma responsiva xs | sm | md | lg | xl`
   *
   * **Valor inicial**: `auto`
   */
  width?: WidthType

  margin?: MarginType

  display?: DisplayType
  /**
   * A propriedade CSS **`object-fit`** define como o conteúdo de um elemento substituído, como um `<img>` ou `<video>`, deve ser redimensionado para caber em seu contêiner.
   *
   * **Sintaxe**: `fill | contain | cover | none | scale-down`
   *
   * **Valor inicial**: `cover`
   */
  objectFit?: React.CSSProperties['objectFit'] | undefined
  /**
   * A propriedade CSS **`object-position`** especifica o alinhamento do conteúdo do elemento substituído selecionado dentro da caixa do elemento. As áreas da caixa que não são cobertas pelo objeto do elemento substituído mostrarão o plano de fundo do elemento.
   *
   * **Sintaxe**: `<position>`
   *
   * **Valor inicial**: `50% 50%`
   */
  objectPosition?: React.CSSProperties['objectPosition'] | undefined
  /**
   * A propriedade **`defaultImageSize`** define o tamanho da imagem padrão.
   *
   * **Sintaxe**: `{ width: string, height: string }`
   *
   * **Valor inicial**: `null`
   */
  defaultImageSize?: DefaultImageSize
  /**
   * A propriedade **`text`** especifica o texto dentro da imagem padrão **`header`**.
   *
   * **Valor inicial**: `null`
   */
  text?: string | undefined
  /**
   * A propriedade **`alt`** fornece informações alternativas para uma imagem se um usuário, por algum motivo, não puder visualizá-la (por causa da conexão lenta, um erro no atributo src ou se o usuário usar um leitor de tela).
   *
   * **Valor inicial**: `null`
   */
  alt?: string
}

const defaultProps: ImageProps = {
  imageSrc: '',
  height: {
    xs: '',
    sm: '',
    md: '',
    lg: '',
    xl: '',
  },
  width: {
    xs: '',
    sm: '',
    md: '',
    lg: '',
    xl: '',
  },
  margin: '',
  display: '',
  objectFit: 'cover',
  objectPosition: 'center',
  defaultImageSize: {
    height: '100%',
    width: '100%',
  },
  text: '',
  alt: '',
}

const Image: FC<ImageProps> = ({
  imageSrc,
  height,
  width,
  margin,
  display,
  objectFit,
  objectPosition,
  defaultImageSize,
  text,
  alt,
}) => {
  const h = typeof height === 'string' ? height : { ...height }
  const w = typeof width === 'string' ? width : { ...width }
  const d = typeof display === 'string' ? display : { ...display }

  return (
    <>
      {imageSrc && imageSrc.length > 0 ? (
        <CardMedia
          component='img'
          sx={{
            height: h,
            width: w,
            display: d,
            userSelect: 'none',
            objectFit: `${objectFit}`,
            objectPosition: `${objectPosition}`,
          }}
          image={imageSrc}
          alt={alt}
        />
      ) : (
        <SVGImage height={defaultImageSize?.height} width={defaultImageSize?.width} text={text} />
      )}
    </>
  )
}

Image.defaultProps = defaultProps

export default Image
